"use client";

import { useQuery } from "@tanstack/react-query";
import imageCompression from "browser-image-compression";
import Image from "next/image";
import { useRouter as useNavigationRouter } from "next/navigation";
import { FC, useCallback, useState } from "react";
import Webcam from "react-webcam";

import { getMyUser } from "@/api";
import VerificationStubSrcWomen from "@/imgs/verification_stub_w.png";
import { Footer, MediaQuery } from "@/shared/components";
import { Button, Loader, Typography } from "@/shared/ui";
import { User } from "@/types";
import { useOnboardingStore } from "stores/onboarding.store";

import { AuthHeader } from "../auth-header";

import { useCompareImages } from "./hooks/compare-images.hook";
import styles from "./verify-photo.module.scss";

const VerifyPhoto: FC = () => {
  const { push } = useNavigationRouter();
  const { mutate, isPending } = useCompareImages();

  const userImageFD = useOnboardingStore((state) => state.userImageFormData);

  const [isCameraOn, setIsCameraOn] = useState<boolean>(false);
  const [webcamRef, setWebcamRef] = useState<Webcam | null>(null);

  const { isLoading } = useQuery<User | undefined>({
    queryKey: ["my-user"],
    queryFn: getMyUser,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const capturePhoto = useCallback(async () => {
    if (webcamRef) {
      const imageSrc = webcamRef.getScreenshot();
      if (imageSrc) {
        try {
          const response = await fetch(imageSrc);
          const blob = await response.blob();

          const file = new File([blob], "photo.png", { type: blob.type });
          const compressedBlob = await imageCompression(file, {
            maxSizeMB: 0.5,
          });
          const compressedFile = new File([compressedBlob], "photo_compressed.png", { type: compressedBlob.type });

          if (userImageFD) {
            const profileImage = userImageFD.get("profileImage");
            if (profileImage instanceof File) {
              const compressedProfileBlob = await imageCompression(profileImage, {
                maxSizeMB: 0.5,
              });
              const compressedProfileFile = new File([compressedProfileBlob], profileImage.name, {
                type: compressedProfileBlob.type,
              });

              userImageFD.set("profileImage", compressedProfileFile);
            }

            userImageFD.append("comparisonImage", compressedFile);
            mutate(userImageFD);
            setIsCameraOn(false);
          }
        } catch (err) {
          console.error("Error capturing or compressing photo: ", err);
        }
      }
    }
  }, [webcamRef, userImageFD, mutate]);

  const onBackHandler = () => {
    if (isCameraOn) {
      setIsCameraOn(false);
    }
    push("/auth/onboarding");
  };

  return (
    <div className={styles.verifyPhotoContainer}>
      <AuthHeader marginBottom={24} onClickBack={onBackHandler} />

      <div className={styles.content}>
        <div className={styles.livePhoto}>
          {isCameraOn ? (
            <Webcam
              ref={(ref) => setWebcamRef(ref)}
              style={{
                borderRadius: 150,
                objectFit: "cover",
              }}
              audio={false}
              screenshotFormat="image/png"
              className={styles.video}
              width={300}
              height={300}
            />
          ) : isLoading ? (
            <Loader border={4} size={40} />
          ) : (
            <Image
              alt="verification-photo-stub"
              width={300}
              height={300}
              className={styles.image}
              src={VerificationStubSrcWomen}
            />
          )}
        </div>

        <div className={styles.messageHint}>
          <Typography bold variant="title-1" align="center">
            Verify yourself
          </Typography>
          <Typography variant="footnote" align="center">
            Ensure authenticity by verifying that you are the person featured in your profile photos
          </Typography>
        </div>

        {isCameraOn ? (
          <Button fullWidth size="big" disabled={isPending} onClick={capturePhoto}>
            Take picture
          </Button>
        ) : (
          <Button fullWidth size="big" onClick={() => setIsCameraOn(true)}>
            Open camera
          </Button>
        )}

        <MediaQuery.Laptop>
          <Footer fixedAtBottom />
        </MediaQuery.Laptop>
      </div>
    </div>
  );
};

export { VerifyPhoto };
